import { ToastConfig } from "@/stores/toast.store";
import { cn } from "@/utils/cn";
import { memo, useEffect, useRef, useState } from "react";

const getToastBgColor = (type: ToastConfig["type"]): string => {
  switch (type) {
    case "error":
      return "bg-red-500/90";
    case "success":
      return "bg-green-500/90";
    case "warning":
      return "bg-orange-500/90";
    case "info":
      return "bg-black";
    default:
      return "bg-black/90";
  }
};

interface CustomToastProps {
  toast: ToastConfig;
  isExiting: boolean;
  onExited: () => void;
}

type GestureExit = {
  x: number;
  y: number;
  phase: "pinned" | "exiting";
  targetX: number;
  targetY: number;
};

export const CustomToast = memo(function CustomToast({ toast, isExiting, onExited }: CustomToastProps) {
  const [offsetX, setOffsetX] = useState(0);
  const [offsetY, setOffsetY] = useState(0);
  const [isDragging, setIsDragging] = useState(false);
  const [gestureExit, setGestureExit] = useState<GestureExit | null>(null);
  const startRef = useRef<{ x: number; y: number } | null>(null);
  const lockedAxisRef = useRef<"x" | "y" | null>(null);
  const exitCalledRef = useRef(false);

  const isGestureDismissing = gestureExit !== null;

  // Two-frame rAF: render at pinned position first (no transition), then animate to off-screen
  useEffect(() => {
    if (gestureExit?.phase !== "pinned") return;
    const rafId = requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        setGestureExit((prev) => (prev ? { ...prev, phase: "exiting" } : null));
      });
    });
    return () => cancelAnimationFrame(rafId);
  }, [gestureExit?.phase]);

  const handlePointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
    if (isGestureDismissing || isExiting) return;
    startRef.current = { x: e.clientX, y: e.clientY };
    setIsDragging(true);
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLDivElement>) => {
    if (!startRef.current || isGestureDismissing || isExiting) return;
    const dx = e.clientX - startRef.current.x;
    const dy = e.clientY - startRef.current.y;

    // Lock axis after the first 6px of movement
    if (!lockedAxisRef.current && (Math.abs(dx) > 6 || Math.abs(dy) > 6)) {
      lockedAxisRef.current = Math.abs(dx) >= Math.abs(dy) ? "x" : "y";
    }

    if (lockedAxisRef.current === "x") {
      setOffsetX(dx);
      setOffsetY(0);
    } else if (lockedAxisRef.current === "y") {
      setOffsetX(0);
      setOffsetY(Math.max(0, dy));
    } else {
      setOffsetX(dx);
      setOffsetY(Math.max(0, dy));
    }
  };

  const finishGesture = () => {
    if (!startRef.current) return;
    const shouldDismiss = Math.abs(offsetX) > 80 || offsetY > 60;
    if (shouldDismiss) {
      const targetX = offsetX > 0 ? 400 : offsetX < -80 ? -400 : 0;
      const targetY = offsetY > 60 ? 300 : 0;
      setGestureExit({ x: offsetX, y: offsetY, phase: "pinned", targetX, targetY });
    } else {
      setOffsetX(0);
      setOffsetY(0);
    }
    setIsDragging(false);
    startRef.current = null;
    lockedAxisRef.current = null;
  };

  const handleTransitionEnd = (e: React.TransitionEvent<HTMLDivElement>) => {
    if (isGestureDismissing && e.propertyName === "opacity" && !exitCalledRef.current) {
      exitCalledRef.current = true;
      onExited();
    }
  };

  const handleAnimationEnd = (e: React.AnimationEvent<HTMLDivElement>) => {
    if (e.animationName === "toast-exit") {
      onExited();
    }
  };

  const dragOpacity = isDragging
    ? Math.max(0.4, 1 - Math.abs(offsetX) / 150 - offsetY / 100)
    : 1;

  let activeStyle: React.CSSProperties = {};
  if (gestureExit) {
    if (gestureExit.phase === "pinned") {
      // Hold at drag position, no transition yet — browser paints this before the rAF fires
      activeStyle = {
        transform: `translateX(${gestureExit.x}px) translateY(${gestureExit.y}px)`,
        opacity: Math.max(0.4, 1 - Math.abs(gestureExit.x) / 150 - gestureExit.y / 100),
        transition: "none",
      };
    } else {
      // Animate from pinned position to off-screen
      activeStyle = {
        transform: `translateX(${gestureExit.targetX}px) translateY(${gestureExit.targetY}px)`,
        opacity: 0,
        transition: "transform 0.28s ease-out, opacity 0.22s ease-out",
      };
    }
  } else if (isDragging) {
    activeStyle = {
      transform: `translateX(${offsetX}px) translateY(${offsetY}px)`,
      opacity: dragOpacity,
      transition: "none",
    };
  }

  return (
    <div
      role="alert"
      aria-live="polite"
      className={cn(
        "pointer-events-auto mx-auto flex min-h-[56px] w-[321px] max-w-[90vw] items-center justify-center rounded-[32px] px-4 py-3 shadow-lg backdrop-blur-[9px] backdrop-filter select-none touch-none",
        getToastBgColor(toast.type),
        !isGestureDismissing && (isExiting ? "animate-toast-exit" : "animate-toast-enter"),
        isDragging ? "cursor-grabbing" : "cursor-grab",
      )}
      style={activeStyle}
      onPointerDown={handlePointerDown}
      onPointerMove={handlePointerMove}
      onPointerUp={finishGesture}
      onPointerCancel={finishGesture}
      onTransitionEnd={handleTransitionEnd}
      onAnimationEnd={handleAnimationEnd}
    >
      <p className="tw-text-body-small flex-1 text-center font-medium whitespace-pre-wrap text-white">
        {toast.message}
      </p>
    </div>
  );
});
